import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Gauge,
  Tag,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Callout,
  EmptyState,
  Select,
  SkeletonRows,
  Stat,
  StatusPill,
  severityTone,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime, humanise } from '../../lib/format';

/**
 * Machine history — health summary plus one chronological stream of everything
 * ODR recorded about this machine.
 *
 * The rule this page exists to honour (directive §6, §27): a metric with no
 * basis renders "Insufficient data", never 0. The server decides that — `value`
 * comes back null with an `unavailableReason` — so the display cannot quietly
 * turn an unknown into a number.
 */

type Tone = 'ok' | 'warn' | 'alarm' | 'neutral';

interface HealthMetric {
  key: string;
  label: string;
  value: number | null;
  unit: string | null;
  unavailableReason: string | null;
  tone: Tone;
  hint: string | null;
}

interface MachineHealth {
  machine: { id: string; code: string; name: string; lineName: string; status: string };
  windowDays: number;
  status: 'NORMAL' | 'ATTENTION' | 'INSUFFICIENT_DATA';
  statusReasons: string[];
  metrics: HealthMetric[];
}

interface TimelineEvent {
  id: string;
  at: string;
  type: string;
  title: string;
  detail: string | null;
  userName: string | null;
  severity: string | null;
  status: string | null;
  sourceType: 'checklist_run' | 'problem_tag' | 'opl' | 'engineering_standard';
  sourceId: string;
}

const EVENT_ICON: Record<string, LucideIcon> = {
  CHECKLIST_STARTED: ClipboardList,
  CHECKLIST_SUBMITTED: ClipboardCheck,
  CHECKLIST_VERIFIED: CheckCircle2,
  CHECKLIST_REJECTED: AlertTriangle,
  ABNORMAL_RESULT: AlertTriangle,
  PROBLEM_CREATED: Tag,
  PROBLEM_ACTION: Wrench,
  PROBLEM_VERIFIED: CheckCircle2,
  PROBLEM_CLOSED: CheckCircle2,
  OPL_CREATED: BookOpen,
  OPL_PUBLISHED: BookOpen,
  OPL_TRAINING: BookOpen,
  STANDARD_REVISION: Gauge,
};

const EVENT_GROUPS: Array<{ label: string; types: string[] }> = [
  { label: 'Everything', types: [] },
  {
    label: 'Checklists',
    types: ['CHECKLIST_STARTED', 'CHECKLIST_SUBMITTED', 'CHECKLIST_VERIFIED', 'CHECKLIST_REJECTED'],
  },
  { label: 'Abnormal results', types: ['ABNORMAL_RESULT'] },
  {
    label: 'Problem tags',
    types: ['PROBLEM_CREATED', 'PROBLEM_ACTION', 'PROBLEM_VERIFIED', 'PROBLEM_CLOSED'],
  },
  { label: 'OPL & training', types: ['OPL_CREATED', 'OPL_PUBLISHED', 'OPL_TRAINING'] },
  { label: 'Engineering standards', types: ['STANDARD_REVISION'] },
];

/** Where a timeline row goes when it is clicked — always the real record. */
function sourceHref(event: TimelineEvent): string {
  switch (event.sourceType) {
    case 'checklist_run':
      return `/checklists/${event.sourceId}/review`;
    case 'problem_tag':
      return `/problems/${event.sourceId}`;
    case 'opl':
      return `/opl/${event.sourceId}`;
    case 'engineering_standard':
      return `/engineering-standards?open=${event.sourceId}`;
  }
}

const PAGE_SIZE = 40;

export function MachineHistoryPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(0);
  const [page, setPage] = useState(0);

  const selected = EVENT_GROUPS[group] ?? EVENT_GROUPS[0]!;
  const typeQuery = selected.types.length > 0 ? `&types=${selected.types.join(',')}` : '';

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['machine', id, 'health'],
    queryFn: () => api.get<MachineHealth>(`/api/analytics/machines/${id}/health`),
  });

  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ['machine', id, 'timeline', selected.label, page],
    queryFn: () =>
      api.get<{ events: TimelineEvent[]; total: number }>(
        `/api/analytics/machines/${id}/timeline?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}${typeQuery}`,
      ),
  });

  const total = timeline?.total ?? 0;
  const shown = page * PAGE_SIZE + (timeline?.events.length ?? 0);

  return (
    <div>
      <PageHeader
        title={
          health ? `${health.machine.code} — history` : 'Machine history'
        }
        subtitle={
          health
            ? `${health.machine.name} · ${health.machine.lineName}`
            : 'Everything ODR has recorded about this machine'
        }
        actions={
          <Button variant="secondary" onClick={() => navigate(`/machines/${id}`)}>
            Back to machine
          </Button>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        {healthLoading ? (
          <SkeletonRows rows={2} />
        ) : health ? (
          <>
            <Callout
              tone={
                health.status === 'ATTENTION'
                  ? 'warn'
                  : health.status === 'NORMAL'
                    ? 'ok'
                    : 'info'
              }
              title={
                health.status === 'INSUFFICIENT_DATA'
                  ? 'Insufficient data to judge this machine'
                  : health.status === 'ATTENTION'
                    ? 'Needs attention'
                    : 'Normal'
              }
            >
              {/*
                The status is never a mystery score: it is the list of rules
                that fired, in words (directive §17).
              */}
              {health.statusReasons.length > 0 ? (
                <>
                  This machine{' '}
                  {health.statusReasons.length === 1
                    ? health.statusReasons[0]
                    : `${health.statusReasons.slice(0, -1).join(', ')} and ${health.statusReasons.at(-1)}`}
                  .
                </>
              ) : (
                <>No attention rule matched over the last {health.windowDays} days.</>
              )}
            </Callout>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {health.metrics.map((metric) => (
                <Stat
                  key={metric.key}
                  label={metric.label}
                  tone={metric.value === null ? 'neutral' : metric.tone}
                  value={
                    metric.value === null ? (
                      <span className="text-base font-semibold text-steel-500">
                        Insufficient data
                      </span>
                    ) : (
                      `${metric.value}${metric.unit ?? ''}`
                    )
                  }
                  hint={metric.unavailableReason ?? metric.hint ?? undefined}
                />
              ))}
            </div>
          </>
        ) : null}

        <Card>
          <CardHeader
            title="Timeline"
            subtitle="Newest first. Every entry is a record — open it to see the original."
            action={
              <Select
                value={String(group)}
                onChange={(event) => {
                  setGroup(Number(event.target.value));
                  setPage(0);
                }}
                aria-label="Filter events"
              >
                {EVENT_GROUPS.map((option, index) => (
                  <option key={option.label} value={index}>
                    {option.label}
                  </option>
                ))}
              </Select>
            }
          />

          {timelineLoading ? (
            <SkeletonRows rows={6} />
          ) : !timeline?.events.length ? (
            <EmptyState
              icon={ClipboardList}
              title="Nothing recorded yet"
              message={
                selected.types.length > 0
                  ? 'No events of this kind on this machine.'
                  : 'No checklist run, Problem Tag or OPL has been recorded against this machine.'
              }
            />
          ) : (
            <ol className="divide-y divide-steel-100">
              {timeline.events.map((event) => {
                const Icon = EVENT_ICON[event.type] ?? ClipboardList;
                return (
                  <li key={event.id}>
                    <Link
                      to={sourceHref(event)}
                      className="flex gap-3 px-4 py-3 transition hover:bg-steel-50"
                    >
                      <Icon
                        aria-hidden
                        className={
                          event.severity === 'CRITICAL' || event.severity === 'ABNORMAL'
                            ? 'mt-0.5 size-4 shrink-0 text-alarm-600'
                            : 'mt-0.5 size-4 shrink-0 text-steel-400'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                          <p className="text-sm font-semibold text-steel-900">{event.title}</p>
                          <p className="shrink-0 text-xs tabular-nums text-steel-500">
                            {formatDateTime(event.at)}
                          </p>
                        </div>
                        {event.detail ? (
                          <p className="mt-0.5 text-sm text-steel-600">{event.detail}</p>
                        ) : null}
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge tone="neutral">{humanise(event.type)}</Badge>
                          {event.severity && event.severity !== 'ABNORMAL' ? (
                            <Badge tone={severityTone(event.severity)}>
                              {humanise(event.severity)}
                            </Badge>
                          ) : null}
                          {event.status ? (
                            <span className="text-xs text-steel-500">
                              {humanise(event.status)}
                            </span>
                          ) : null}
                          {event.userName ? (
                            <span className="text-xs text-steel-500">· {event.userName}</span>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}

          {total > PAGE_SIZE ? (
            <div className="flex items-center justify-between gap-2 border-t border-steel-100 px-4 py-3">
              <p className="text-xs text-steel-500">
                Showing {shown} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Newer
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={shown >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Older
                </Button>
              </div>
            </div>
          ) : null}
        </Card>

        {health ? (
          <p className="text-center text-xs text-steel-400">
            Health metrics cover the last {health.windowDays} days.{' '}
            <StatusPill tone="neutral" label={humanise(health.machine.status)} /> is the machine's
            configured status, set on the machine record.
          </p>
        ) : null}
      </div>
    </div>
  );
}
